import { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { identifyUser, resetUser } from '@/lib/posthog';
import { useEntitlements } from './use-entitlements';

/**
 * Attaches the analytics identity once per signed-in session.
 *
 * Identity is the psychologist UUID and nothing else — no e-mail, no name.
 * `plan` and `is_internal` ride along as person properties so funnels can be
 * segmented by plan and our own team's sessions can be filtered out of every
 * report. Waiting for entitlements means one `identify` call carrying complete
 * properties rather than two partial ones.
 *
 * It also owns the other half of that contract: dropping the identity when the
 * session is gone. See the reset branch below.
 *
 * Mounted once, in Pages(). Do not call it anywhere else.
 */
export function useAnalyticsIdentity() {
    const { user, status, isLoading, isAuthenticated, isInternal } = useAuth();
    const { plan } = useEntitlements({ enabled: isAuthenticated });
    const identifiedKey = useRef<string | null>(null);
    const hasResetForAnonymous = useRef(false);

    useEffect(() => {
        if (!user?.id) {
            identifiedKey.current = null;

            // Sessions do not only end through the logout button, which is the
            // one path AuthContext covers: an expired cookie, a server-side
            // logout, or simply opening /sign-in in a browser that was signed
            // in yesterday all land here. PostHog persists the identified
            // distinct_id across page loads, so without this the pageviews on
            // the sign-in screen — and whoever signs in next on this machine —
            // stay attributed to the psychologist who already left.
            //
            // Only 'anonymous': `pending_verification` and `blocked_conflict`
            // have no `user` either, but they are still that person's session.
            // And only once bootstrap has resolved — `status` reads as
            // 'anonymous' while the query is in flight, so resetting without
            // the isLoading guard would fire on every single page load.
            if (!isLoading && status === 'anonymous' && !hasResetForAnonymous.current) {
                hasResetForAnonymous.current = true;
                resetUser();
            }

            return;
        }

        hasResetForAnonymous.current = false;

        // Re-identify when the plan changes (trial → paid), but not on every
        // render or entitlement refetch.
        const key = `${user.id}:${plan ?? 'unknown'}:${isInternal}`;
        if (identifiedKey.current === key) return;
        identifiedKey.current = key;

        identifyUser(user.id, { is_internal: isInternal, plan });
    }, [user?.id, plan, isInternal, isLoading, status]);
}
